declare const window: { env?: Record<string, unknown>; location?: { origin: string } };

const env = window['env'] || {};

/**
 * Reads a runtime variable as a string, treating an unsubstituted `${VAR}` placeholder
 * as absent.
 *
 * Values are not necessarily strings: `envsubst` writes quoted strings into `env.js` at
 * container startup, but the checked-in `assets/env.js` used for local development sets
 * `debug` as a real boolean. So coerce before inspecting — this runs at module load, and
 * anything thrown here takes the whole app down before it renders.
 */
function read(key: string): string {
  const value = env[key];
  if (value === undefined || value === null || value === '') return '';
  const text = String(value);
  return text.startsWith('${') ? '' : text;
}

/** Falls back to deriving the socket origin from the API URL (`http→ws`, `https→wss`). */
function getWebSocketUrl(httpUrl: string): string {
  if (!httpUrl) return '';
  return httpUrl.replace(/^http/, 'ws');
}

const apiUrl = read('apiUrl');

/**
 * One deployment per customer: there is no tenant resolution in the client. The
 * instance is described entirely by these runtime variables, injected into
 * `assets/env.js` at container startup, so the same image ships to every
 * customer. Never hardcode a hostname here.
 */
export const environment = {
  name: read('profile') || 'dev',
  production: read('profile') === 'production',
  appVersion: read('appVersion') || '1.0.0',
  /** Customer slug for this instance, e.g. `acme`. Empty in local development. */
  tenantName: read('tenantName'),
  /** Public origin of this instance, e.g. `https://acme.staffmetrics.com`. */
  appUrl: read('appUrl') || window.location?.origin || '',
  apiUrl,
  wsUrl: read('wsUrl') || getWebSocketUrl(apiUrl),
  debug: read('debug') === 'true',
};
